import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { NextStep, NextStepAction } from "../../../../utils/prospectNextStep";

interface NextStepBarProps {
  step: NextStep;
  busy?: boolean;
  onRun: (action: NextStepAction) => void;
}

/**
 * What to do next with this prospect, at the top of its screen.
 *
 * The screen used to lead with the stage overrides — advance, flag, resign —
 * while the action that actually moves the customer on sat inside a panel below
 * the fold. This is the opposite order: one filled button for the step the stage
 * calls for, the step in words beside it, and the overrides demoted to a menu
 * (see StatusActionsBar).
 *
 * The button does not replace the panel's own: it hands over to it, opening the
 * tab it lives in and starting the same confirmation there. The panel is where
 * the context is — which stations, which access data, what the customer is about
 * to be sent — so the decision is still taken in front of it.
 *
 * Where the next move is the customer's, there is no button: a step that reads
 * "wait for the signature" must not be dressed as something ops can do.
 */
const NextStepBar: React.FC<NextStepBarProps> = ({ step, busy, onRun }) => {
  const navigate = useNavigate();
  if (!step.description && !step.action && !step.warning) return null;

  return (
    <>
      {step.warning && (
        <Message
          severity="warn"
          className="w-full mb-2 justify-content-start"
          text={step.warning}
          data-testid="next-step-warning"
        />
      )}
      <div
        className={`next-step-bar mb-3 flex flex-wrap align-items-center justify-content-between gap-3${
          step.waiting ? " next-step-bar--waiting" : ""
        }`}
        data-testid="next-step-bar"
      >
        <div className="flex align-items-center gap-2 flex-wrap">
          <i
            className={`pi ${step.waiting ? "pi-clock" : "pi-flag-fill"} next-step-icon`}
          />
          <span className="next-step-label">
            {step.waiting ? "Waiting" : "Next step"}
          </span>
          {step.description && (
            <span className="next-step-description">{step.description}</span>
          )}
        </div>
        {/* What support waits on is fixed on another page, e.g. mapping a wallbox model (CWA-231). */}
        {!step.action && step.href && step.label && (
          <Button
            label={step.label}
            icon="pi pi-external-link"
            iconPos="right"
            onClick={() => navigate(step.href as string)}
          />
        )}
        {step.action && step.label && (
          <Button
            label={step.label}
            icon="pi pi-arrow-right"
            iconPos="right"
            // Offered, not urged, while the normal course is still to wait.
            outlined={step.waiting}
            loading={busy}
            disabled={busy}
            onClick={() => onRun(step.action as NextStepAction)}
          />
        )}
      </div>
    </>
  );
};

export default NextStepBar;
